import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronRight, ArrowLeft, Circle, CircleDot, Target, Crosshair, Gauge, Clock } from "lucide-react";

interface PriorityStepProps {
  initialImpact: number;
  initialUrgency: number;
  onNext: (impact: number, urgency: number) => void;
  onBack: () => void;
}

const PRIORITY_OPTIONS = [
  { value: 2, label: "Geringer Einfluss", desc: "Hat wenig Auswirkung auf Ergebnisse", icon: Circle },
  { value: 5, label: "Mittlerer Einfluss", desc: "Beeinflusst Teilbereiche", icon: CircleDot },
  { value: 8, label: "Hoher Einfluss", desc: "Beeinflusst wichtige Kennzahlen", icon: Target },
  { value: 10, label: "Existenziell", desc: "Geschäftsentscheidende Auswirkung", icon: Crosshair },
];

const URGENCY_OPTIONS = [
  { value: 2, label: "Zeit vorhanden", desc: "Wochen oder Monate", icon: Circle },
  { value: 5, label: "Diese Woche", desc: "In den nächsten Tagen", icon: CircleDot },
  { value: 8, label: "Heute oder morgen", desc: "Sehr bald", icon: Target },
  { value: 10, label: "Sofort", desc: "Jetzt", icon: Crosshair },
];

export const PriorityStep = ({ initialImpact, initialUrgency, onNext, onBack }: PriorityStepProps) => {
  const [impact, setImpact] = useState(initialImpact || 0);
  const [urgency, setUrgency] = useState(initialUrgency || 0);

  const isValid = impact > 0 && urgency > 0;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Gauge className="h-5 w-5" />
          </div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Schritt 4 von 4</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-foreground">
          Wie stark beeinflusst diese Entscheidung euer Ergebnis?
        </h2>
        <p className="text-muted-foreground text-base">
          Hilft uns, die Analyse richtig zu priorisieren
        </p>
      </div>

      <div className="space-y-8">
        {/* Impact */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-base">Einfluss auf euer Ergebnis</h3>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {PRIORITY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = impact === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setImpact(option.value)}
                  className={cn(
                    "group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 text-left",
                    "bg-card/50 backdrop-blur-sm",
                    isSelected
                      ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                      : "border-border/60 hover:border-primary/40 hover:bg-card"
                  )}
                >
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                    isSelected 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                  )}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      "font-semibold text-sm transition-colors",
                      isSelected ? "text-foreground" : "text-foreground/90"
                    )}>{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Urgency */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-base">Wie schnell braucht ihr Klarheit?</h3>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {URGENCY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = urgency === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setUrgency(option.value)}
                  className={cn(
                    "group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 text-left",
                    "bg-card/50 backdrop-blur-sm",
                    isSelected
                      ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                      : "border-border/60 hover:border-primary/40 hover:bg-card"
                  )}
                >
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                    isSelected 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                  )}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      "font-semibold text-sm transition-colors",
                      isSelected ? "text-foreground" : "text-foreground/90"
                    )}>{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border/60 hover:bg-card hover:border-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={() => onNext(impact, urgency)}
          disabled={!isValid}
          className="flex-1 gap-2 h-12 rounded-xl"
        >
          Analyse starten
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
